import { useState } from 'react';
import { motion } from 'framer-motion';
import { FiPlus, FiMinus, FiTrash2, FiShoppingCart } from 'react-icons/fi';
import Modal from './Modal';
import { useQuote } from '../contexts/QuoteContext';

const honeypotStyle = {
  position: 'absolute',
  left: '-9999px',
  width: 0,
  height: 0,
  opacity: 0,
  overflow: 'hidden',
};

const QuoteModal = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot — must stay empty

  const {
    quoteItems,
    clientInfo,
    updateQuantity,
    updateRentalPeriod,
    removeItem,
    updateClientInfo,
    clearQuote
  } = useQuote();


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateClientInfo({ [name]: value });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);

    const fullName = `${clientInfo.firstName || ''} ${clientInfo.lastName || ''}`.trim();

    const eventTypeMap = {
      wedding: 'Wedding',
      corporate: 'Corporate',
      birthday: 'Birthday',
      anniversary: 'Anniversary',
      other: 'Other'
    };

    // Compose message: leader + event details (interested items go in the structured field)
    const parts = ['Quote request — see interested items below'];
    if (clientInfo.eventType) parts.push(`Event type: ${eventTypeMap[clientInfo.eventType] || clientInfo.eventType}`);
    if (clientInfo.eventDate) parts.push(`Event date: ${clientInfo.eventDate}`);
    if (clientInfo.venue) parts.push(`Venue: ${clientInfo.venue}`);
    if (clientInfo.guestCount) parts.push(`Guest count: ${clientInfo.guestCount}`);
    const composedMessage = parts.join('\n\n');

    const interestedItems = quoteItems.map(item => ({
      name: item.productData?.name || 'Unknown item',
      quantity: item.quantity,
      days: item.rentalPeriod,
    }));

    try {
      const response = await fetch('/api/enquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fullName,
          email: clientInfo.email,
          phone: clientInfo.phone,
          message: composedMessage,
          source: 'quote-builder',
          interestedItems,
          website,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(() => {
          setSubmitSuccess(false);
          clearQuote();
          onClose();
        }, 5000);
      } else {
        setSubmitError(data.error || 'Something went wrong. Please try again or call 0466 645 060.');
      }
    } catch (err) {
      setSubmitError('Network error. Please check your connection and try again, or call 0466 645 060.');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Modal isOpen={isOpen} onClose={onClose} size="95%" title="Request Quote">
      <div className="p-6">
        <div className="max-h-[80vh] overflow-y-auto">
        {submitSuccess ? (
          <div className="py-16 text-center">
            <h3 className="text-2xl font-bold text-neutral-800 mb-3">Thank you!</h3>
            <p className="text-neutral-600 mb-2">
              We've received your quote request and will be in touch within 24–48 hours.
            </p>
            <p className="text-neutral-600">
              If your enquiry is urgent, please call us on <strong>0466 645 060</strong>.
            </p>
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Quote Items */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">
              Your Quote Items ({quoteItems.length})
            </h3>

            {quoteItems.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <FiShoppingCart size={48} className="mx-auto mb-4 opacity-50" />
                <p>Your quote is empty</p>
                <p className="text-sm">Add some products to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {quoteItems.map((item) => (
                   <div key={item.id} className="flex gap-4 p-4 bg-neutral-50 rounded-lg">
                     <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                       {item.productData?.images?.[0] ? (
                         <img
                           src={typeof item.productData.images[0] === 'string'
                             ? item.productData.images[0].replace('http://127.0.0.1:5001', '')
                             : item.productData.images[0].url?.replace('http://127.0.0.1:5001', '')}
                           alt={item.productData.name}
                           className="w-full h-full object-cover rounded-lg"
                         />
                       ) : (
                         <span className="text-lg">🌸</span>
                       )}
                     </div>

                     <div className="flex-grow">
                       <h4 className="font-medium text-neutral-800">{item.productData?.name || 'Unknown Product'}</h4>
                       {item.variantData && (
                         <p className="text-sm text-neutral-600">
                           {item.variantData.attributes?.map(attr => `${attr.name}: ${attr.value}`).join(', ')}
                         </p>
                       )}

                      <div className="flex items-center gap-4 mt-2">
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-neutral-600">Qty:</label>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="w-6 h-6 rounded bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center"
                            >
                              <FiMinus size={12} />
                            </button>
                            <span className="w-8 text-center text-sm">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="w-6 h-6 rounded bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center"
                            >
                              <FiPlus size={12} />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <label className="text-xs text-neutral-600">Days:</label>
                          <select
                            value={item.rentalPeriod}
                            onChange={(e) => updateRentalPeriod(item.id, parseInt(e.target.value))}
                            className="text-sm border rounded px-2 py-1"
                          >
                            <option value={1}>1 day</option>
                            <option value={2}>2 days</option>
                            <option value={3}>3 days</option>
                            <option value={7}>1 week</option>
                            <option value={14}>2 weeks</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex justify-end items-center mt-2">
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Client Information Form */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">Contact Information</h3>

            <form id="quote-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot — must remain empty */}
              <div style={honeypotStyle} aria-hidden="true">
                <label htmlFor="website-qm">Website (leave blank)</label>
                <input
                  type="text"
                  id="website-qm"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm">{submitError}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={clientInfo.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-inset focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={clientInfo.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-inset focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={clientInfo.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-inset focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={clientInfo.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-inset focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Event Type *
                  </label>
                  <select
                    name="eventType"
                    value={clientInfo.eventType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-inset focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select event type</option>
                    <option value="wedding">Wedding</option>
                    <option value="corporate">Corporate</option>
                    <option value="birthday">Birthday</option>
                    <option value="anniversary">Anniversary</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Event Date *
                  </label>
                  <input
                    type="date"
                    name="eventDate"
                    value={clientInfo.eventDate}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-inset focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Venue
                </label>
                <input
                  type="text"
                  name="venue"
                  value={clientInfo.venue}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-inset focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Venue name and location"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Guest Count
                </label>
                <input
                  type="number"
                  name="guestCount"
                  value={clientInfo.guestCount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-inset focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Approximate number of guests"
                />
              </div>
            </form>
          </div>
        </div>
        )}

        {/* Submit Buttons */}
        {!submitSuccess && (
          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue Shopping
            </button>
            <button
              onClick={clearQuote}
              disabled={isSubmitting}
              className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear Quote
            </button>
            <button
              type="submit"
              form="quote-form"
              disabled={isSubmitting}
              className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Submit Quote Request'}
            </button>
          </div>
        )}
        </div>
      </div>
    </Modal>
  );
};

export default QuoteModal;
